import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

export default function Questionnaire() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, valid: false, used: false });
  const [areas, setAreas] = useState([]);
  const [clientName, setClientName] = useState("");
  const [sector, setSector] = useState("");
  const [contact, setContact] = useState("");
  const [scores, setScores] = useState({});
  const [areaNotes, setAreaNotes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/readiness/questionnaire/${token}`)
      .then(({ data }) => {
        if (data.used) { setState({ loading: false, valid: true, used: true }); setAreas(data.areas || []); return; }
        setAreas(data.areas || []);
        setClientName(data.client_name || "");
        setSector(data.sector || "");
        const init = {}; (data.areas || []).forEach((a) => { init[a.key] = 3; });
        setScores(init);
        setState({ loading: false, valid: true, used: false });
      })
      .catch(() => setState({ loading: false, valid: false, used: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/readiness/questionnaire/${token}`, { client_name: clientName, sector, contact, scores, area_notes: areaNotes });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-background">
      <div className="px-8 py-6 flex items-center gap-2" style={{ backgroundColor: "#002FA7" }}>
        <ShieldCheck className="w-6 h-6 text-white" />
        <span className="text-white font-heading font-black tracking-tight">AI &amp; Sales Readiness</span>
      </div>
      <div className="max-w-2xl mx-auto p-6 md:p-10">{children}</div>
    </div>
  );

  if (!state.valid) return <Shell><div className="border border-destructive/30 bg-destructive/5 p-6"><h2 className="font-heading font-bold text-lg">Link non valido</h2><p className="text-sm text-muted-foreground mt-2">Questo questionario non esiste o è scaduto.</p></div></Shell>;
  if (state.used || done) return (
    <Shell>
      <div className="border border-border bg-white p-10 text-center">
        <CheckCircle2 className="w-12 h-12 text-[#00A859] mx-auto" />
        <h2 className="font-heading font-black text-2xl tracking-tight mt-4">{done ? "Grazie!" : "Questionario già compilato"}</h2>
        <p className="text-sm text-muted-foreground mt-2">{done ? "Le tue risposte sono state inviate al consulente." : "Questo link è già stato utilizzato."}</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <h1 className="font-heading font-black text-3xl tracking-tight">Questionario di readiness</h1>
      <p className="text-sm text-muted-foreground mt-2">Valuta la tua azienda su 5 aree. Per ciascuna, sposta il cursore da 1 (situazione iniziale) a 5 (situazione ottimale). Ci vogliono ~5 minuti.</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div className="bg-white border border-border p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label className="data-label">Azienda</Label><Input data-testid="q-client-input" className="mt-1 rounded-none" value={clientName} onChange={(e) => setClientName(e.target.value)} required /></div>
          <div><Label className="data-label">Settore</Label><Input className="mt-1 rounded-none" value={sector} onChange={(e) => setSector(e.target.value)} /></div>
          <div><Label className="data-label">Referente</Label><Input className="mt-1 rounded-none" value={contact} onChange={(e) => setContact(e.target.value)} /></div>
        </div>

        {areas.map((a) => (
          <div key={a.key} className="bg-white border border-border p-5">
            <div className="flex items-center justify-between">
              <span className="font-heading font-semibold tracking-tight">{a.name}</span>
              <span className="font-mono font-bold text-xl text-primary" data-testid={`q-score-${a.key}`}>{scores[a.key] ?? 3}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 italic">{a.guide_question}</p>
            <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 list-disc pl-4">
              {a.questions.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
            <Slider className="mt-4" value={[scores[a.key] ?? 3]} min={1} max={5} step={1} onValueChange={([v]) => setScores({ ...scores, [a.key]: v })} data-testid={`q-slider-${a.key}`} />
            <div className="grid grid-cols-3 gap-2 mt-2 text-[10px] text-muted-foreground">
              <span><b>1</b> {a.anchors["1"]}</span>
              <span className="text-center"><b>3</b> {a.anchors["3"]}</span>
              <span className="text-right"><b>5</b> {a.anchors["5"]}</span>
            </div>
            <Input className="mt-3 rounded-none text-xs h-8" placeholder="Note (opzionale)" value={areaNotes[a.key] || ""} onChange={(e) => setAreaNotes({ ...areaNotes, [a.key]: e.target.value })} />
          </div>
        ))}

        <Button type="submit" disabled={submitting} className="rounded-none w-full" data-testid="q-submit-btn">
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Invia questionario
        </Button>
      </form>
    </Shell>
  );
}
